import { TranslatedGameCategory, TranslatedLeague, TranslatedSport } from '@arland-bmnext/api-data'
import { HubConnectionState } from '@microsoft/signalr'
import React, { useEffect, useState } from 'react'
import { useCommonContext } from '../../../context/common.context'
import { useLiveContext } from '../../../context/live.context'
import { sortArray } from '../../../util/array'
import { GamesListMemo } from '../GamesList'
import { GamesListSportFilter } from '../GamesListSportFilter'
import NoItems from '../../../public/no-items.svg'
import LoadingIndicator from '../../core/LoadingIndicator'
import useTranslation from 'next-translate/useTranslation'

type LiveGamesListProps = {
  isVisible?: boolean
  maxGames?: number
  showOpenGameCount?: boolean
}

const LiveGamesList = ({ isVisible = false, maxGames, showOpenGameCount }: LiveGamesListProps) => {
  const { t } = useTranslation('bets')
  const liveContext = useLiveContext()
  const commonContext = useCommonContext()

  const [sports, setSports] = useState<TranslatedSport[]>([])
  const [leagues, setLeagues] = useState<TranslatedLeague[]>([])
  const [gameCategories, setGameCategories] = useState<TranslatedGameCategory[]>([])

  const sportId = liveContext.liveListActiveSportId
  const setSportId = liveContext.setLiveListActiveSportId

  useEffect(() => {
    handleLiveSportsChange()
  }, [liveContext.sports, liveContext.liveGames])

  useEffect(() => {
    buildListData()
  }, [sportId, liveContext.liveGames])

  const handleLiveSportsChange = () => {
    const liveSports = liveContext.sports?.filter((sport) => sport.openGameCount > 0)
    if (sportId == null && liveSports.length > 0) {
      setSportId(liveSports[0].id)
    } else if (sportId != null && liveSports.length > 0 && liveSports.find((sport) => sport.id === sportId) == null) {
      setSportId(liveSports[0].id)
    } else if (liveSports.length === 0) {
      setSportId(null)
      setLeagues([])
    }
    setSports(liveSports)
  }

  const buildListData = () => {
    let _leagues = new Set<TranslatedLeague>()
    let _gameCategories = new Set<TranslatedGameCategory>()
    let sortLeaguesByCategory = []
    let _leaguesArray = []

    liveContext.liveGames
      ?.filter((game) => game.sportId === sportId)
      ?.forEach((game) => {
        const league = liveContext.leagues.get(game.leagueId.toString())
        const gameCategory = liveContext.gameCategories.get(game.gameCategoryId.toString())
        if (league) {
          if (!_leagues.has(league)) sortLeaguesByCategory.push(gameCategory ? gameCategory.sortKey : 0)
          _leagues.add(league)
        }
        if (gameCategory) _gameCategories.add(gameCategory)
      })

    _leaguesArray = Array.from(_leagues.values())
    _leaguesArray = _leaguesArray.sort(
      (a, b) => sortLeaguesByCategory[_leaguesArray.indexOf(b)] - sortLeaguesByCategory[_leaguesArray.indexOf(a)],
    )
    setLeagues(sortArray(_leaguesArray, 'sortKey', 'desc'))
    setGameCategories(sortArray(Array.from(_gameCategories.values()), 'sortKey', 'desc'))
  }

  const getCountryCodeForLeague = (league: TranslatedLeague) => {
    const leagueGame = liveContext.liveGames?.find((game) => game.leagueId === league.id)
    const categoryId = leagueGame?.gameCategoryId
    if (categoryId) {
      const category = gameCategories?.find((category) => category.id === categoryId)
      if (category) {
        const country = commonContext.countries?.find((country) => country.id === category.countryId)
        if (country) return country.shortSign
      }
    }
    return null
  }

  const liveGamesToDisplay =
    maxGames > 0
      ? liveContext.liveGames?.filter((game) => game.sportId === sportId).slice(0, maxGames)
      : liveContext.liveGames?.filter((game) => game.sportId === sportId)

  return (
    <div className="live-games-list flex flex-col grow w-full">
      {sports?.length > 0 && (
        <GamesListSportFilter
          sports={sports}
          activeSportId={sportId}
          setActiveSportId={setSportId}
          showOpenGameCount={showOpenGameCount}
          className={`live-games-list-sport-filter !px-2 md:!px-4 pb-3 pt-0 ${isVisible ? '' : 'md:hidden'}`}
        />
      )}

      <div className="live-games-list-items flex flex-col space-y-2 md:py-4">
        {leagues?.map((league) => {
          const countryCode = getCountryCodeForLeague(league)
          const liveGames = liveGamesToDisplay.filter((game) => game.leagueId === league.id)

          return (
            <React.Fragment key={league.id}>
              {liveGames?.length > 0 && (
                <GamesListMemo
                  liveGames={liveGames}
                  countryCode={countryCode}
                  leagueName={league.name}
                  leagueId={league.id}
                  titleType="leagueName"
                  iconType="country"
                  deepLinkLeague
                  gamesListClassName="live-games-list-item md:rounded-md md:mx-4 md:w-[calc(100%-32px)]"
                />
              )}
            </React.Fragment>
          )
        })}
      </div>

      {(liveContext.liveGames?.length == null || liveContext.liveGames?.length === 0) &&
        liveContext.connectionState === HubConnectionState.Connected && (
          <div className="flex flex-col grow justify-center items-center w-full px-8 py-12">
            <div className="flex flex-col justify-center space-y-4 w-3/4 max-w-[300px]">
              <NoItems className="flex w-full" />
              <span className="text-center">{t('GamesList.noEventsAvailable')}</span>
            </div>
          </div>
        )}

      {(liveContext.connectionState === HubConnectionState.Connecting ||
        liveContext.connectionState === HubConnectionState.Reconnecting) && (
        <div className="flex flex-col grow justify-center items-center w-full px-8 py-12">
          <LoadingIndicator />
        </div>
      )}
    </div>
  )
}

export default LiveGamesList
